import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import PropTypes from "prop-types";

export default function MarksEntryDialog({ open, onClose, examScheduleMasterId, courseRow }) {
  const [marks, setMarks] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [internalExaminer, setInternalExaminer] = useState("");
  const [externalExaminer, setExternalExaminer] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  /* =====================
     RESET ON OPEN
     ===================== */
  useEffect(() => {
    if (!open || !courseRow?.student_rollnos) return;

    const initial = {};
    courseRow.student_rollnos.forEach((r) => (initial[r] = ""));
    setMarks(initial);
    setFieldErrors({});
    setValidationError("");
    setInternalExaminer("");
    setExternalExaminer("");
  }, [open, courseRow]);

  /* =====================
   LOAD EXISTING MARKS
   ===================== */
  useEffect(() => {
    if (!open || !courseRow?.student_rollnos?.length) return;

    const fetchExisting = async () => {
      try {
        setLoading(true);

        const res = await axios.post(
          `${API_BASE_URL}/api/external-labmarks/existing/${clientId}/${collegeId}/${examScheduleMasterId}/existing`,
          {
            courses: [
              {
                timetable_id: courseRow.timetable_id, // 🔑 timetable_id
                student_rollnos: courseRow.student_rollnos,
              },
            ],
          }
        );

        if (res.data?.success && res.data.data?.length > 0) {
          const courseData = res.data.data[0];

          const records = courseData.records || [];

          // ==========================================
          // LOAD EXISTING MARKS
          // ==========================================
          setMarks((prev) => {
            const updated = { ...prev };

            records.forEach((r) => {
              if (r.marks === -1 || String(r.marks) === "-1") {
                updated[r.student_rollno] = "AB";
              } else if (r.marks !== null && r.marks !== undefined && r.marks !== "") {
                updated[r.student_rollno] = String(r.marks);
              } else {
                updated[r.student_rollno] = "";
              }
            });

            return updated;
          });

          // ==========================================
          // LOAD EXAMINER NAMES
          // ==========================================
          const examiner = courseData.examiner || {};

          setInternalExaminer(examiner.internal_examiner_name || "");

          setExternalExaminer(examiner.external_examiner_name || "");
        }
      } catch (err) {
        console.error("Existing marks fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchExisting();
  }, [open, courseRow]);

  /* =====================
     HANDLE CHANGE
     ===================== */
  const handleMarkChange = (rollno, value) => {
    const max = Number(courseRow.external_maximum);
    const val = value.toUpperCase();

    if (val === "") {
      setMarks((p) => ({ ...p, [rollno]: "" }));
      setFieldErrors((p) => ({ ...p, [rollno]: "Required" }));
      return;
    }

    if (val === "A" || val === "AB") {
      setMarks((p) => ({ ...p, [rollno]: val === "A" ? "A" : "AB" }));
      setFieldErrors((p) => ({ ...p, [rollno]: "" }));
      return;
    }

    if (!/^\d+$/.test(val)) {
      setFieldErrors((p) => ({ ...p, [rollno]: "Only AB or numbers" }));
      return;
    }

    const num = Number(val);
    if (num < 0 || num > max) {
      setFieldErrors((p) => ({ ...p, [rollno]: `0 - ${max}` }));
      return;
    }
    if (!internalExaminer.trim()) {
      setValidationError("Enter Internal Examiner name");
      return false;
    }

    if (!externalExaminer.trim()) {
      setValidationError("Enter External Examiner name");
      return false;
    }
    setMarks((p) => ({ ...p, [rollno]: val }));
    setFieldErrors((p) => ({ ...p, [rollno]: "" }));
  };

  /* =====================
     VALIDATE
     ===================== */
  const validateAll = () => {
    const min = Number(courseRow.external_minimum);
    const max = Number(courseRow.external_maximum);

    for (const rollno of courseRow.student_rollnos) {
      const v = marks[rollno];

      if (!v) {
        setValidationError(`Enter marks for ${rollno}`);
        return false;
      }

      if (v === "AB") continue;
      if (v === "A") {
        setValidationError(`Incomplete AB for ${rollno}`);
        return false;
      }

      const num = Number(v);
      if (num < min || num > max) {
        setValidationError(`Marks must be ${min}-${max} for ${rollno}`);
        return false;
      }
    }

    setValidationError("");
    return true;
  };

  /* =====================
     SAVE
     ===================== */
  const handleSave = async () => {
    if (!validateAll()) return;

    const payload = courseRow.student_rollnos.map((rollno) => ({
      internal_examiner: internalExaminer,
      external_examiner: externalExaminer,
      timetable_id: Number(courseRow.timetable_id),
      exam_course_id: Number(courseRow.exam_course_id),
      year_number: Number(courseRow.year_number),
      semester_number: Number(courseRow.semester_number),
      course_code: courseRow.course_code,
      student_rollno: rollno,
      marks: marks[rollno] === "AB" ? -1 : Number(marks[rollno]),
      fac_confirm: 1,
      hod_confirm: 0,
      coe_confirm: 0,
      created_by: user?.user_email,
      modified_by: user?.user_email,
      //examScheduleMasterId: examScheduleMasterId,  🔑 Pass the examScheduleMasterId to the backend
    }));

    setLoading(true);
    await axios.post(
      `${API_BASE_URL}/api/external-labmarks/${clientId}/${collegeId}/${examScheduleMasterId}/save`,
      {
        marks: payload,
      }
    );
    setLoading(false);
    alert("Marks saved successfully ✅");
    onClose();
  };

  const absentCount = Object.values(marks).filter((v) => v === "AB").length;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }

        onClose();
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Marks Entry — {courseRow?.course_code} - {courseRow?.course_name}- Maximum Marks:{" "}
        {courseRow?.external_maximum}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <Box>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Internal Examiner Name"
                    value={internalExaminer}
                    onChange={(e) => setInternalExaminer(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="External Examiner Name"
                    value={externalExaminer}
                    onChange={(e) => setExternalExaminer(e.target.value)}
                    required
                  />
                </Grid>
              </Grid>
            </Box>

            <Typography sx={{ mb: 1, fontWeight: 600 }}>
              Absent Students: {absentCount} Mark Absent as AB
            </Typography>

            <Grid container spacing={2} sx={{ fontWeight: 600 }}>
              <Grid item xs={2}>
                S.No
              </Grid>
              <Grid item xs={5}>
                Roll No
              </Grid>
              <Grid item xs={3}>
                Marks
              </Grid>
            </Grid>

            {courseRow?.student_rollnos?.map((rollno, i) => (
              <Grid container spacing={2} alignItems="center" key={rollno} sx={{ py: 1 }}>
                <Grid item xs={2}>
                  {i + 1}
                </Grid>
                <Grid item xs={5}>
                  {rollno}
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    size="medium"
                    value={marks[rollno] ?? ""}
                    onChange={(e) => handleMarkChange(rollno, e.target.value.trim())}
                    error={Boolean(fieldErrors[rollno])}
                    helperText={fieldErrors[rollno] || ""}
                    onBlur={() => {
                      if (marks[rollno] === "A") {
                        setMarks((p) => ({ ...p, [rollno]: "AB" }));
                      }
                    }}
                    inputProps={{
                      style: {
                        textAlign: "center",
                        fontSize: "18px", // 🔥 increase input font size
                        fontWeight: 400, // optional (bolder)
                      },
                    }}
                    sx={{ width: 100 }}
                  />
                </Grid>
              </Grid>
            ))}
          </Box>
        )}
      </DialogContent>

      {validationError && <Box sx={{ color: "error.main", px: 3 }}>⚠️ {validationError}</Box>}

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#b71c1c",
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!internalExaminer || !externalExaminer}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Faculty Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MarksEntryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  courseRow: PropTypes.object,
  clientId: PropTypes.number,
  collegeId: PropTypes.number,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
